import { useMutation, useQuery } from "@tanstack/react-query";

import { useAxios, useApiResponseParser } from "../";

import type { IManufacturerProduct } from "../../interfaces";

export default function useManufacturerProduct() {
    const { axiosPrivate } = useAxios();
    const { parseSuccessResponseData } = useApiResponseParser();

    function useList(manufacturerId?: string, categoryId?: string, isActive?: boolean) {
        return useQuery({
            queryKey: ["manufacturer-products", manufacturerId, categoryId, isActive],
            queryFn: async () => {
                const params = new URLSearchParams();
                if (manufacturerId) params.append("ManufacturerId", manufacturerId);
                if (categoryId) params.append("CategoryId", categoryId);
                if (isActive !== undefined) params.append("IsActive", String(isActive));

                const response = await axiosPrivate.get(
                    `/v1/manufacture-products/list${params.toString() ? `?${params.toString()}` : ""}`
                );
                return parseSuccessResponseData(response);
            },
            enabled: true, // Always enabled to allow fetching all products
        });
    }

    function useCreate() {
        return useMutation({
            mutationFn: async (data: {
                Manufacturers_Id: string;
                Name: string;
                Category_Id?: string;
                Category?: string;
                Subcategory?: string;
                Color?: string;
                Description?: string;
                UnitType?: string;
                UnitCoverage?: number;
                IsActive?: boolean;
            }) => {
                const response = await axiosPrivate.post<IManufacturerProduct>("/v1/manufacture-products/create", data);
                return parseSuccessResponseData(response);
            },
        });
    }

    function useUpdate() {
        return useMutation({
            mutationFn: async (data: {
                Id: string;
                Manufacturers_Id?: string;
                Name?: string;
                Category_Id?: string;
                Category?: string;
                Subcategory?: string;
                Color?: string;
                Description?: string;
                UnitType?: string;
                UnitCoverage?: number;
                IsActive?: boolean;
            }) => {
                const response = await axiosPrivate.post<IManufacturerProduct>("/v1/manufacture-products/update", data);
                return parseSuccessResponseData(response);
            },
        });
    }

    function useDelete() {
        return useMutation({
            mutationFn: async (id: string) => {
                const response = await axiosPrivate.delete(`/v1/manufacture-products/delete/${id}`);
                return parseSuccessResponseData(response);
            },
        });
    }

    return {
        useList,
        useCreate,
        useUpdate,
        useDelete,
    };
}
